/**
 * NeoEmployeesPanel — NEO (Manager Control Center) main surface.
 *
 * Renders:
 *  - Grid of employee boxes (name + card #) — tap opens that card's NEO session
 *  - "Select All" → bulk-action sheet (change URL | send voucher | enable/disable feature)
 *  - Media quick-access (Google / Facebook / Instagram / Website / TripAdvisor)
 *
 * Mobile-first: 390×844 portrait-safe. Bilingual via react-i18next.
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    Users, CheckSquare, Square, Gift, Link as LinkIcon, ToggleLeft,
    ExternalLink, Loader2, DollarSign, Check, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { stringifyDetail } from '../../lib/utils';

const API_BASE = window.location.origin;

const PLATFORMS = [
    { value: 'google_reviews', label: 'Google' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'yelp', label: 'Yelp' },
    { value: 'tripadvisor', label: 'TripAdvisor' },
    { value: 'trustpilot', label: 'Trustpilot' },
];

const BULK_FEATURES = [
    { value: 'landing_page_enabled', labelKey: 'neo.featLanding', labelFallback: 'Landing page' },
    { value: 'global_review_enabled', labelKey: 'neo.featGlobalReview', labelFallback: 'Global Review' },
    { value: 'gift_card_enabled', labelKey: 'neo.featGiftCard', labelFallback: 'Gift Card' },
    { value: 'review_counter_enabled', labelKey: 'neo.featReviewCounter', labelFallback: 'Review Counter' },
    { value: 'require_employee_pin', labelKey: 'neo.featEmployeePin', labelFallback: 'Employee PIN' },
];

const REASON_CODES = [
    { code: 'birthday', label: 'Birthday' },
    { code: 'vip', label: 'VIP' },
    { code: 'manager_goodwill', label: 'Goodwill' },
    { code: 'long_wait', label: 'Long wait' },
    { code: 'other', label: 'Other' },
];

export default function NeoEmployeesPanel({ cardId, sessionToken, employees, onRefresh }) {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(() => new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkTab, setBulkTab] = useState('url'); // 'url' | 'voucher' | 'feature'
    const [submitting, setSubmitting] = useState(false);

    // URL
    const [urlPlatform, setUrlPlatform] = useState('google_reviews');
    // Voucher
    const [voucherValue, setVoucherValue] = useState('25');
    const [voucherReason, setVoucherReason] = useState('birthday');
    const [voucherMessage, setVoucherMessage] = useState('');
    const [voucherDays, setVoucherDays] = useState('30');
    // Feature
    const [featureKey, setFeatureKey] = useState('landing_page_enabled');
    const [featureEnabled, setFeatureEnabled] = useState(true);

    // Media quick access
    const [mediaOpen, setMediaOpen] = useState(false);
    const [mediaDestinations, setMediaDestinations] = useState([]);
    const [mediaLoading, setMediaLoading] = useState(false);

    const authHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'X-Manager-Session': sessionToken,
    }), [sessionToken]);

    // Show ALL employees (with or without assigned cards). Those without a
    // card still appear in the grid for visibility and demo-select support;
    // bulk actions resolve them to an empty card list at run-time (with a
    // toast if zero selectable cards remain).
    const allEmployees = employees || [];

    // Selection key: real card_id if present, else synthetic `nc:<employee_id>`
    // so no-card employees can still be visually selected for demo purposes.
    const selectionKeyFor = (emp) => emp.primary_card_id || `nc:${emp.employee_id}`;

    const toggleOne = (selKey) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(selKey)) next.delete(selKey); else next.add(selKey);
            return next;
        });
    };
    const toggleAll = () => {
        if (selected.size === allEmployees.length) setSelected(new Set());
        else setSelected(new Set(allEmployees.map(selectionKeyFor)));
    };

    const openBulk = () => {
        if (selected.size === 0) {
            toast.error(t('neo.selectFirst', 'Select at least one employee first'));
            return;
        }
        setBulkOpen(true);
    };

    const runBulk = async () => {
        // Filter down to only real card IDs (drop nc:* synthetic demo keys)
        const ids = Array.from(selected).filter(k => !k.startsWith('nc:'));
        const skipped = selected.size - ids.length;
        if (ids.length === 0) {
            toast.error(t('neo.noCardsInSelection', 'No cards in selection. Assign cards to employees first.'));
            return;
        }
        if (skipped > 0) {
            toast(`${t('neo.skippedNoCard', 'Skipping')} ${skipped} ${t('neo.skippedNoCardTail', 'employee(s) without cards')}`);
        }
        setSubmitting(true);
        try {
            let url = '', body = {};
            if (bulkTab === 'url') {
                url = `${API_BASE}/api/neo/bulk/url`;
                body = { card_ids: ids, platform: urlPlatform };
            } else if (bulkTab === 'voucher') {
                const v = parseFloat(voucherValue);
                if (!v || v <= 0) { toast.error(t('neo.voucherValueInvalid', 'Enter a valid voucher amount')); setSubmitting(false); return; }
                url = `${API_BASE}/api/neo/bulk/voucher`;
                body = { card_ids: ids, value: v, reason_code: voucherReason, message: voucherMessage || null, expires_in_days: parseInt(voucherDays, 10) || 30 };
            } else if (bulkTab === 'feature') {
                url = `${API_BASE}/api/neo/bulk/feature`;
                body = { card_ids: ids, feature: featureKey, enabled: featureEnabled };
            }
            const res = await fetch(url, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(stringifyDetail(err.detail) || 'failed');
            }
            const data = await res.json();
            toast.success(`${t('neo.bulkDone', 'Applied to')} ${data.updated ?? data.count ?? ids.length} ${t('neo.bulkCards', 'cards')}`);
            setBulkOpen(false);
            setSelected(new Set());
            if (onRefresh) onRefresh();
        } catch (e) {
            toast.error(`${t('neo.bulkFailed', 'Bulk action failed')}: ${e.message || ''}`);
        }
        setSubmitting(false);
    };

    const loadMedia = async () => {
        setMediaLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/neo/media/${cardId}`, { headers: authHeaders() });
            if (res.ok) {
                const d = await res.json();
                setMediaDestinations(d.destinations || []);
            }
        } catch { /* silent */ }
        setMediaLoading(false);
    };

    const openMedia = () => {
        setMediaOpen(true);
        if (mediaDestinations.length === 0) loadMedia();
    };

    return (
        <div className="space-y-4" data-testid="neo-employees-panel">
            {/* Top control bar */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-fuchsia-400" />
                    <h3 className="text-sm font-semibold text-white">{t('neo.employees', 'Employees')}</h3>
                    <span className="text-xs text-slate-500">{allEmployees.length}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleAll}
                        className="border-slate-700 text-slate-200 hover:bg-slate-800 min-h-[36px]"
                        data-testid="neo-select-all"
                    >
                        {selected.size === allEmployees.length && allEmployees.length > 0 ? (
                            <CheckSquare className="w-4 h-4 mr-1 text-fuchsia-400" />
                        ) : (
                            <Square className="w-4 h-4 mr-1" />
                        )}
                        {t('neo.selectAll', 'Select All')}
                    </Button>
                    <Button
                        size="sm"
                        onClick={openBulk}
                        disabled={selected.size === 0}
                        className="bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-slate-700 min-h-[36px]"
                        data-testid="neo-bulk-open"
                    >
                        {t('neo.bulk', 'Bulk')} ({selected.size})
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={openMedia}
                        className="border-slate-700 text-slate-200 hover:bg-slate-800 min-h-[36px]"
                        data-testid="neo-media-open"
                    >
                        <ExternalLink className="w-4 h-4 mr-1" /> {t('neo.media', 'Media')}
                    </Button>
                </div>
            </div>

            {/* Grid */}
            {allEmployees.length === 0 ? (
                <div className="text-center py-10 px-4 border border-dashed border-slate-700 rounded-xl bg-slate-900/40">
                    <Users className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-slate-300 mb-1">{t('neo.emptyTitle', 'No employees connected yet')}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">
                        {t('neo.emptyBody', 'Add employees from the Admin portal, then assign them NFC cards to unlock bulk actions here.')}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="neo-employee-grid">
                    {allEmployees.map((emp, idx) => {
                        const hasCard = !!emp.primary_card_id;
                        const selKey = selectionKeyFor(emp);
                        const cardLabel = hasCard
                            ? (emp.card_ids && emp.card_ids.length > 1
                                ? `${emp.primary_card_id} +${emp.card_ids.length - 1}`
                                : emp.primary_card_id)
                            : t('neo.noCard', 'No card assigned');
                        const isSel = selected.has(selKey);
                        const seqNumber = String(idx + 1).padStart(3, '0');
                        return (
                            <div
                                key={emp.employee_id}
                                className={`relative rounded-xl border p-3 transition-all ${isSel ? 'bg-fuchsia-600/15 border-fuchsia-500/60 shadow-md shadow-fuchsia-900/30' : hasCard ? 'bg-slate-900/70 border-slate-800 hover:border-slate-700' : 'bg-slate-900/40 border-slate-800/60'}`}
                                data-testid={`neo-emp-${emp.employee_id}`}
                            >
                                <button
                                    onClick={() => toggleOne(selKey)}
                                    className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center"
                                    data-testid={`neo-emp-check-${emp.employee_id}`}
                                    aria-label="Select employee"
                                >
                                    {isSel
                                        ? <CheckSquare className="w-5 h-5 text-fuchsia-400" />
                                        : <Square className="w-5 h-5 text-slate-500" />}
                                </button>
                                {hasCard ? (
                                    <a
                                        href={`/manager/${emp.primary_card_id}`}
                                        className="block pr-6"
                                        data-testid={`neo-emp-open-${emp.employee_id}`}
                                    >
                                        <p className="text-sm font-semibold text-white truncate">{emp.name}</p>
                                        <p className="text-[10px] text-slate-500 font-mono mt-0.5">#{seqNumber}</p>
                                        <p className="text-[11px] text-fuchsia-300 font-mono mt-0.5 truncate">{cardLabel}</p>
                                        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400">
                                            <span className="inline-flex items-center gap-1"><DollarSign className="w-3 h-3" />{emp.incentive_balance}</span>
                                            <span>·</span>
                                            <span>{emp.taps_today} taps</span>
                                        </div>
                                    </a>
                                ) : (
                                    <div className="block pr-6">
                                        <p className="text-sm font-semibold text-white truncate">{emp.name}</p>
                                        <p className="text-[10px] text-slate-500 font-mono mt-0.5">#{seqNumber}</p>
                                        <p className="text-[11px] text-slate-500 mt-0.5 truncate italic">{cardLabel}</p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Bulk action dialog */}
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-sm" data-testid="neo-bulk-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('neo.bulkTitle', 'Bulk Action')}</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            {t('neo.bulkDescription', 'Applying to')} {selected.size} {t('neo.bulkCards', 'cards')}.
                        </DialogDescription>
                    </DialogHeader>
                    {/* Tab selector */}
                    <div className="grid grid-cols-3 gap-1 bg-slate-800 rounded-lg p-1">
                        {[
                            { k: 'url', icon: LinkIcon, label: t('neo.bulkUrl', 'URL') },
                            { k: 'voucher', icon: Gift, label: t('neo.bulkVoucher', 'Voucher') },
                            { k: 'feature', icon: ToggleLeft, label: t('neo.bulkFeature', 'Feature') },
                        ].map(({ k, icon: Icon, label }) => (
                            <button
                                key={k}
                                onClick={() => setBulkTab(k)}
                                className={`py-2 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-1 ${bulkTab === k ? 'bg-fuchsia-600 text-white' : 'text-slate-400 hover:text-white'}`}
                                data-testid={`neo-bulk-tab-${k}`}
                            >
                                <Icon className="w-3.5 h-3.5" /> {label}
                            </button>
                        ))}
                    </div>
                    {bulkTab === 'url' && (
                        <div className="space-y-2">
                            <label className="text-xs text-slate-400">{t('neo.bulkUrlLabel', 'Destination platform')}</label>
                            <Select value={urlPlatform} onValueChange={setUrlPlatform}>
                                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="neo-bulk-url-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                                    {PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {bulkTab === 'voucher' && (
                        <div className="space-y-2">
                            <div>
                                <label className="text-xs text-slate-400">{t('neo.voucherValue', 'Amount ($)')}</label>
                                <Input type="number" min="1" value={voucherValue} onChange={(e) => setVoucherValue(e.target.value)} className="bg-slate-800 border-slate-700" data-testid="neo-voucher-value" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">{t('neo.voucherReason', 'Reason')}</label>
                                <Select value={voucherReason} onValueChange={setVoucherReason}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="neo-voucher-reason"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                                        {REASON_CODES.map(r => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">{t('neo.voucherDays', 'Valid for (days)')}</label>
                                <Input type="number" min="1" value={voucherDays} onChange={(e) => setVoucherDays(e.target.value)} className="bg-slate-800 border-slate-700" data-testid="neo-voucher-days" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">{t('neo.voucherMessage', 'Message (optional)')}</label>
                                <Input value={voucherMessage} onChange={(e) => setVoucherMessage(e.target.value)} className="bg-slate-800 border-slate-700" data-testid="neo-voucher-msg" />
                            </div>
                        </div>
                    )}
                    {bulkTab === 'feature' && (
                        <div className="space-y-2">
                            <label className="text-xs text-slate-400">{t('neo.bulkFeatureLabel', 'Feature')}</label>
                            <Select value={featureKey} onValueChange={setFeatureKey}>
                                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="neo-feature-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                                    {BULK_FEATURES.map(f => <SelectItem key={f.value} value={f.value}>{t(f.labelKey, f.labelFallback)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <div className="flex items-center gap-2 pt-1">
                                <Button
                                    size="sm"
                                    onClick={() => setFeatureEnabled(true)}
                                    className={`flex-1 ${featureEnabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-800 hover:bg-slate-700'}`}
                                    data-testid="neo-feature-on"
                                >
                                    <Check className="w-4 h-4 mr-1" /> {t('neo.enable', 'Enable')}
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => setFeatureEnabled(false)}
                                    className={`flex-1 ${!featureEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-800 hover:bg-slate-700'}`}
                                    data-testid="neo-feature-off"
                                >
                                    <X className="w-4 h-4 mr-1" /> {t('neo.disable', 'Disable')}
                                </Button>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setBulkOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800 w-full sm:w-auto" data-testid="neo-bulk-cancel">
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={runBulk}
                            disabled={submitting}
                            className="bg-fuchsia-600 hover:bg-fuchsia-700 w-full sm:w-auto"
                            data-testid="neo-bulk-confirm"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                            {t('neo.confirmApply', 'Confirm & Apply')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Media dialog */}
            <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-sm" data-testid="neo-media-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('neo.mediaTitle', 'Media — Quick Access')}</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            {t('neo.mediaDescription', 'Tap to open the destination immediately.')}
                        </DialogDescription>
                    </DialogHeader>
                    {mediaLoading ? (
                        <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" /></div>
                    ) : mediaDestinations.length === 0 ? (
                        <p className="text-sm text-slate-400 py-4 text-center">{t('neo.noMedia', 'No destinations configured yet.')}</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-2">
                            {mediaDestinations.map(d => (
                                <a
                                    key={d.key}
                                    href={d.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg p-3 text-center transition-colors"
                                    data-testid={`neo-media-${d.key}`}
                                >
                                    <p className="text-sm font-semibold text-white">{d.label}</p>
                                    <p className="text-[10px] text-slate-500 truncate mt-0.5">{d.url.replace(/^https?:\/\//, '')}</p>
                                </a>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
